import React from "react";
import { ConversationMessagesItems } from "../../../models/ConversationMessages";
import CustomAudioPlayer from "../../../components/audio/CustomAudioPlayer";
import {
  fileNameFromUrl,
  formatHour,
  formatWhatsappText,
  getMediaKind,
  getSpecialMessage,
  MediaKind,
  resolveMediaUrl,
  statusIcon,
} from "./utils";

interface MessageBubbleProps {
  message: ConversationMessagesItems;
  onOpenMedia: (url: string, kind: MediaKind) => void;
  onMediaLoad?: () => void;
  onRetry?: (message: ConversationMessagesItems) => void;
}

const TEMPLATE_BUTTON_ICONS: Record<number, string> = {
  1: "reply-fill",
  2: "box-arrow-up-right",
  3: "telephone-fill",
  4: "copy",
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onOpenMedia, onMediaLoad, onRetry }) => {
  const outbound = message.type === "outbound";
  const status = statusIcon(message.whatsapp_statuses_id, message.local_status);

  const mediaUrl = message.local_media_url ?? resolveMediaUrl(message.media_url);
  const mediaKind: MediaKind | null = message.local_media_kind
    ?? (message.media_url ? getMediaKind(message.media_url, message.content_type) : null);
  const special = getSpecialMessage(message.body, message.content_type);
  const body = special ? "" : (message.body ?? "");

  const meta = (
    <span className="wa-meta">
      {formatHour(message.created_at)}
      {outbound && <i className={`bi bi-${status.icon} ms-1 ${status.className}`} title={status.label} aria-label={status.label} />}
    </span>
  );

  const renderMedia = () => {
    if (!mediaUrl || !mediaKind) return null;
    switch (mediaKind) {
      case "image":
        return (
          <button type="button" className="wa-media-button" onClick={() => onOpenMedia(mediaUrl, "image")} aria-label="Ver imagen">
            <img src={mediaUrl} alt="Imagen" className="wa-media" loading="lazy" onLoad={onMediaLoad} />
          </button>
        );
      case "video":
        return <video src={mediaUrl} className="wa-media" controls preload="metadata" onLoadedMetadata={onMediaLoad} />;
      case "audio":
        return message.local_media_url
          ? <audio src={mediaUrl} controls className="wa-audio" />
          : <div className="wa-audio"><CustomAudioPlayer src={mediaUrl} /></div>;
      default:
        return (
          <a className="wa-document" href={mediaUrl} target="_blank" rel="noopener noreferrer" download>
            <i className="bi bi-file-earmark-text fs-3" />
            <span className="text-truncate">{message.local_file_name ?? fileNameFromUrl(message.media_url)}</span>
            <i className="bi bi-download ms-auto" />
          </a>
        );
    }
  };

  const renderTemplate = () => {
    const template = message.template;
    const buttons = template?.buttons ?? [];
    return (
      <>
        {template?.header_format === "TEXT" && template.header_content && <div className="fw-semibold mb-1">{template.header_content}</div>}
        {template?.header_format === "IMAGE" && template.header_content && (
          <button type="button" className="wa-media-button mb-1" onClick={() => onOpenMedia(resolveMediaUrl(template.header_content), "image")}>
            <img src={resolveMediaUrl(template.header_content)} alt="Encabezado de la plantilla" className="wa-media" onLoad={onMediaLoad} />
          </button>
        )}
        {template?.header_format === "VIDEO" && template.header_content && (
          <video src={resolveMediaUrl(template.header_content)} className="wa-media mb-1" controls preload="metadata" />
        )}
        <div className="wa-text" dangerouslySetInnerHTML={{ __html: formatWhatsappText(body) }} />
        {template?.footer_content && <div className="wa-footer-text">{template.footer_content}</div>}
        {meta}
        {buttons.length > 0 && (
          <div className="wa-template-buttons">
            {buttons.slice(0, buttons.length > 3 ? 2 : 3).map((button, index) => (
              <div key={index} className="wa-template-button">
                <i className={`bi bi-${TEMPLATE_BUTTON_ICONS[button.type] ?? "reply-fill"} me-2`} />
                {button.text}
              </div>
            ))}
            {buttons.length > 3 && (
              <div className="wa-template-button"><i className="bi bi-list-ul me-2" />Ver todas las opciones</div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={`wa-row ${outbound ? "wa-row--out" : "wa-row--in"}`}>
      <div
        className={`wa-bubble ${outbound ? "wa-bubble--out" : "wa-bubble--in"} ${message.whatsapp_templates_id ? "wa-bubble--template" : ""} ${message.local_status === "failed" ? "wa-bubble--failed" : ""}`}
      >
        {message.whatsapp_templates_id ? (
          <>
            <div className="wa-template-badge"><i className="bi bi-megaphone me-1" />Plantilla</div>
            {renderTemplate()}
          </>
        ) : (
          <>
            {renderMedia()}
            {special && (
              <div className="wa-special"><i className={`bi bi-${special.icon} me-2`} />{special.label}</div>
            )}
            {body && <div className="wa-text" dangerouslySetInnerHTML={{ __html: formatWhatsappText(body) }} />}
            {!body && !special && !mediaKind && <div className="wa-text fst-italic text-body-secondary">Mensaje vacío</div>}
            {meta}
          </>
        )}

        {message.reactions && message.reactions.length > 0 && (
          <div className="wa-reactions" aria-label="Reacciones">
            {message.reactions.map((reaction) => (
              <span key={reaction.id}>{reaction.body}</span>
            ))}
          </div>
        )}
      </div>
      {message.local_status === "failed" && onRetry && (
        <button type="button" className="btn btn-link btn-sm text-danger p-0 ms-2 align-self-center" onClick={() => onRetry(message)}>
          Reintentar
        </button>
      )}
    </div>
  );
};
